import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { useClientLookup } from '@/entities/client/model/queries'
import {
  CHAT_COPY,
  chatErrorMessage,
  useBroadcastReach,
  useSendBroadcast,
  useSendShiftBroadcast,
  useBroadcastShifts,
  useShiftBroadcastReach,
} from '@/entities/chat'
import { cn } from '@/shared/lib/utils'
import { ShiftAudiencePicker } from './ShiftAudiencePicker'
import { isSameGuardSet } from './broadcastableShifts'

const MAX_BODY_LENGTH = 4000

const broadcastSchema = z.object({
  clientId: z.number().optional(),
  body: z.string().min(1, 'Message is required').max(MAX_BODY_LENGTH, 'Message is too long'),
})

type Audience = 'client' | 'shift'

type BroadcastFormData = z.infer<typeof broadcastSchema>

export function BroadcastComposer({
  onSent,
  onCancel,
}: {
  onSent: (broadcastId: number) => void
  onCancel: () => void
}) {
  const { data: clients, isLoading: isLoadingClients } = useClientLookup()
  const sendBroadcast = useSendBroadcast()
  const sendShiftBroadcast = useSendShiftBroadcast()
  const [sendError, setSendError] = useState<string | null>(null)
  const [audience, setAudience] = useState<Audience>('client')
  const [shiftId, setShiftId] = useState<number | null>(null)
  const [pickedGuardIds, setPickedGuardIds] = useState<number[]>([])
  const [audienceError, setAudienceError] = useState<string | null>(null)
  const broadcastShifts = useBroadcastShifts(audience === 'shift')

  const form = useForm<BroadcastFormData>({
    resolver: zodResolver(broadcastSchema),
    defaultValues: {
      clientId: undefined as unknown as number,
      body: '',
    },
  })

  const watchedClientId = form.watch('clientId')
  const body = form.watch('body') ?? ''
  const rosterGuardIds = (broadcastShifts.data ?? []).find((shift) => shift.shiftId === shiftId)?.guards.map((guard) => guard.userId) ?? []
  const selectedGuardIds = pickedGuardIds.filter((id) => rosterGuardIds.includes(id))

  const guardSubset = isSameGuardSet(selectedGuardIds, rosterGuardIds) ? null : selectedGuardIds
  const clientReachQuery = useBroadcastReach(audience === 'client' ? (watchedClientId ?? null) : null)
  const shiftReachQuery = useShiftBroadcastReach(
    audience === 'shift' && selectedGuardIds.length > 0 ? shiftId : null,
    guardSubset,
  )
  const reachQuery = audience === 'client' ? clientReachQuery : shiftReachQuery
  const reachTarget = audience === 'client' ? watchedClientId : selectedGuardIds.length > 0 ? shiftId : null
  const isSending = sendBroadcast.isPending || sendShiftBroadcast.isPending

  const onSubmit = async (data: BroadcastFormData) => {
    setSendError(null)
    setAudienceError(null)
    if (audience === 'client' && !data.clientId) {
      form.setError('clientId', { message: 'Client is required' })
      return
    }
    if (audience === 'shift' && shiftId === null) {
      setAudienceError(CHAT_COPY.pickAShift)
      return
    }
    if (audience === 'shift' && selectedGuardIds.length === 0) {
      setAudienceError(CHAT_COPY.pickAGuard)
      return
    }
    try {
      const broadcast =
        audience === 'client'
          ? await sendBroadcast.mutateAsync({ clientId: data.clientId as number, body: data.body })
          : await sendShiftBroadcast.mutateAsync({ shiftId: shiftId as number, body: data.body, guardUserIds: guardSubset })
      onSent(broadcast.id)
    } catch (error) {
      setSendError(chatErrorMessage(error, CHAT_COPY.failed))
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <p className="text-[13.5px] font-semibold text-ink">{CHAT_COPY.newBroadcast}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <div className="mb-3 inline-flex rounded-md border border-line p-0.5">
          {(['client', 'shift'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setAudience(option)
                setAudienceError(null)
                setSendError(null)
              }}
              className={cn(
                'rounded px-3 py-1 text-[12.5px] font-medium',
                audience === option ? 'bg-brand-accent text-white' : 'text-ink-3 hover:bg-hover',
              )}
            >
              {option === 'client' ? 'Client' : 'Guards on a shift'}
            </button>
          ))}
        </div>
        <p className="mb-3 text-[12px] text-ink-3">
          {audience === 'client' ? CHAT_COPY.newBroadcastHint : CHAT_COPY.newShiftBroadcastHint}
        </p>
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {audience === 'shift' ? (
                <ShiftAudiencePicker
                  shifts={broadcastShifts.data ?? []}
                  isLoading={broadcastShifts.isLoading}
                  isError={broadcastShifts.isError}
                  onRetry={() => void broadcastShifts.refetch()}
                  shiftId={shiftId}
                  onShiftChange={(nextShiftId, guardIds) => {
                    setShiftId(nextShiftId)
                    setPickedGuardIds(guardIds)
                    setAudienceError(null)
                  }}
                  selectedGuardIds={selectedGuardIds}
                  onSelectedGuardIdsChange={(guardIds) => {
                    setPickedGuardIds(guardIds)
                    setAudienceError(null)
                  }}
                />
              ) : (
              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client *</FormLabel>
                    <Select
                      value={field.value ? String(field.value) : ''}
                      disabled={isLoadingClients}
                      onValueChange={(value) => field.onChange(Number(value))}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={isLoadingClients ? 'Loading clients...' : 'Select a client'} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {clients?.map((client) => (
                          <SelectItem key={client.id} value={String(client.id)}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              )}

              {audienceError && <p className="text-[12.5px] text-bad">{audienceError}</p>}

              {reachTarget != null &&
                (reachQuery.isLoading ? (
                  <p className="text-[12.5px] text-ink-3">Checking reach…</p>
                ) : reachQuery.isError ? (
                  <p className="text-[12.5px] text-bad">
                    {CHAT_COPY.reachFailed}{' '}
                    <button
                      type="button"
                      onClick={() => void reachQuery.refetch()}
                      className="underline underline-offset-2"
                    >
                      {CHAT_COPY.retry}
                    </button>
                  </p>
                ) : reachQuery.data ? (
                  <p className="text-[12.5px] text-ink-3">{reachQuery.data.description}</p>
                ) : null)}

              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message *</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={6}
                        maxLength={MAX_BODY_LENGTH}
                        placeholder="Write the broadcast message..."
                        {...field}
                      />
                    </FormControl>
                    <p className="text-right text-[11px] text-ink-4">{`${body.length} / ${MAX_BODY_LENGTH}`}</p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {sendError && (
                <div className="rounded-md border border-warn-bd bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
                  {sendError}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSending}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isSending}>
                  {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send broadcast
                </Button>
              </div>
            </form>
          </Form>
      </div>
    </div>
  )
}
